import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getNestedSubelementsAndFloatingElementsByType, useSlsDispatchContext, useSlsStateContext } from '@fs/zion-sls'
import { arraysEqual } from '../utils/utils'
import { getBulkStandardizedFullNames } from '../../../../shared/services/standards/names'
/**
 * Custom hook to handle localizing names when image arks or images change.
 * @param {Object} params
 * @param {Array} params.slsImageArksToLoad - The image arks to load.
 * @param {Object} params.currentImage - The current image object.
 * @param {boolean} params.loadingSls - Whether SLS is loading.
 * @param {string} params.imageArk - Current image ark.
 */
export function useLocalizeNames({ slsImageArksToLoad, currentImage, loadingSls, imageArk }) {
  const zionSlsDispatch = useSlsDispatchContext()
  const zionSlsState = useSlsStateContext()
  const { images: slsImages } = zionSlsState
  const [localizedImages, setLocalizedImages] = useState([])

  const needToLocalizeNames = useRef(false)
  const previousImageArksToLoad = useRef([])
  const previousArk = useRef(imageArk)

  const loadedPartitionKeys = useMemo(() => slsImages?.map((img) => img?.partitionKey) || [], [slsImages])
  const areAllNeededImagesLoaded = useMemo(() => {
    return slsImageArksToLoad?.every((ark) => loadedPartitionKeys.includes(ark))
  }, [slsImageArksToLoad, loadedPartitionKeys])

  const localizeNames = useCallback(async (slsState, slsDispatch, image) => {
    const personsToLocalize = getNestedSubelementsAndFloatingElementsByType(image, 'PERSON')
    const personsInfo = await getBulkStandardizedFullNames(personsToLocalize)

    slsDispatch({
      type: 'SET_PERSON_LOCALIZED_NAMES',
      value: {
        partitionKey: slsState.currentImageArk,
        personsInfo,
      },
    })
    setLocalizedImages((prevImages) => [...prevImages, slsState.currentImageArk])
  }, [])

  // Track changes in image arks to load
  useEffect(() => {
    if (!arraysEqual(slsImageArksToLoad, previousImageArksToLoad.current)) {
      previousImageArksToLoad.current = slsImageArksToLoad
      needToLocalizeNames.current = true
    }
  }, [slsImageArksToLoad])

  // Track imageArk changes, for example when switching images
  useEffect(() => {
    if (imageArk !== previousArk.current) {
      previousArk.current = imageArk
      needToLocalizeNames.current = true
    }
  }, [imageArk])

  // Localize names when needed
  useEffect(() => {
    if (
      currentImage?.partitionKey && // Check if currentImage is defined
      needToLocalizeNames.current && // Check if we need to localize names
      !loadingSls && // Ensure SLS data has finished loading
      areAllNeededImagesLoaded && // Ensure all needed images are loaded
      !localizedImages.includes(currentImage.partitionKey) // Ensure we haven't already localized this image
    ) {
      localizeNames(zionSlsState, zionSlsDispatch, currentImage)
      needToLocalizeNames.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImage, loadingSls, areAllNeededImagesLoaded, localizeNames, localizedImages])
}
